import {
  importFromXlsx,
  type ImportXlsxOptions,
} from '@/shared/utils/xlsxExport';

/** CSV·엑셀 업로드 input accept 공통 값 */
export const SPREADSHEET_UPLOAD_ACCEPT =
  '.csv,text/csv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel';

export const isSpreadsheetFile = (file: File) =>
  /\.(xlsx|xls)$/i.test(file.name) ||
  file.type.includes('spreadsheet') ||
  file.type === 'application/vnd.ms-excel';

export const isCsvFile = (file: File) =>
  /\.csv$/i.test(file.name) ||
  file.type === 'text/csv' ||
  file.type.includes('csv');

/**
 * 헤더 맵 기준으로 CSV 또는 엑셀 파일을 객체 배열로 읽는다.
 * 양식 다운로드가 xlsx인 업로드 화면에서 CSV·엑셀을 동일하게 처리할 때 사용한다.
 */
export const readRowsWithHeaderMap = async (
  file: File,
  headerMap: Record<string, string>,
  options: ImportXlsxOptions = {},
): Promise<Record<string, unknown>[]> => {
  if (isSpreadsheetFile(file) && !isCsvFile(file)) {
    return await importFromXlsx(file, headerMap, options);
  }

  const text = await readFileAsText(file);
  const rows = parseCsvWithHeaderMap(text, headerMap);
  return rows.map((row) => ({ ...row }));
};

/** CSV 한 줄을 필드 배열로 파싱한다 (따옴표·쉼표 이스케이프 지원). */
export const parseCsvLine = (line: string): string[] => {
  const fields: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (inQuotes) {
      if (char === '"') {
        if (line[i + 1] === '"') {
          current += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        current += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === ',') {
      fields.push(current);
      current = '';
    } else {
      current += char;
    }
  }

  fields.push(current);
  return fields;
};

/**
 * CSV 텍스트를 `{ key: label }` 헤더 맵 기준으로 객체 배열로 변환한다.
 * 헤더 행은 한글 라벨(`label`)이어야 한다.
 */
export const parseCsvWithHeaderMap = (
  csvText: string,
  headerMap: Record<string, string>,
): Record<string, string>[] => {
  const lines = csvText
    .replace(/^\uFEFF/, '')
    .split(/\r?\n/)
    .filter((line) => line.trim().length > 0);

  if (lines.length < 2) {
    throw new Error('CSV에 데이터 행이 없습니다.');
  }

  const headerRow = parseCsvLine(lines[0]).map((h) => h.trim());
  const labelToKey = Object.fromEntries(
    Object.entries(headerMap).map(([key, label]) => [label.trim(), key]),
  );

  const expectedLabels = Object.values(headerMap);
  const missing = expectedLabels.filter((label) => !headerRow.includes(label));
  if (missing.length > 0) {
    throw new Error(`필수 헤더가 없습니다: ${missing.join(', ')}`);
  }

  return lines.slice(1).map((line) => {
    const cells = parseCsvLine(line);
    const row: Record<string, string> = {};
    headerRow.forEach((header, index) => {
      const key = labelToKey[header];
      if (key) {
        row[key] = (cells[index] ?? '').trim();
      }
    });
    return row;
  });
};

/** File 객체를 텍스트로 읽는다. */
export const readFileAsText = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error('파일 읽기 실패'));
    reader.readAsText(file, 'UTF-8');
  });
